import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import { IChartProps } from '../../../../../component/charts/types';
import { labelFormatterPercentileSymbol } from '../../../../../shared/formatters/formatters';
import { OwnersBySchoolingChartData } from '../types/realtyTransactions.types';

export const OwnersBySchooling: React.FC = () => {
  const { requestStatusT, chartsDataTransactions } = useSelector(
    (state: any) => state.realtyTransactionsState
  );
  return (
    <ChartCard
      data={chartsDataTransactions['owners_by_schooling']}
      cy={'OwnersBySchooling'}
      id={'ownersBySchooling'}
      Chart={OwnersBySchoolingChart}
      hasAnnualDataOnly
      requestStatus={requestStatusT['migratoryOrigin']}
    />
  );
};

const OwnersBySchoolingChart: React.FC<
  IChartProps<OwnersBySchoolingChartData[]>
> = ({ data, cy, id }) => {
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [
    labelFormatterPercentileSymbol,
    labelFormatterPercentileSymbol,
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        position: 'relative',
      }}
    >
      <ResponsiveContainer
        width="100%"
        height="90%"
        className="chart-container"
      >
        <BarChart
          layout="vertical"
          data={data}
          id={id}
          data-cy={`${cy}Chart`}
          onClick={() => setHasClicked(!hasClicked)}
        >
          <Tooltip
            wrapperStyle={{ visibility: 'visible' }}
            content={
              <StickyTooltip
                hasClicked={hasClicked}
                recordTooltip={recordTooltip}
                setRecordTooltip={setRecordTooltip}
                stopChange={stopChange}
                setStopChange={setStopChange}
                formatterFunctions={formatterFunctions}
              />
            }
          />
          <YAxis
            dataKey="schooling"
            type="category"
            orientation="left"
            mirror
            axisLine={false}
            tickLine={false}
            tick={<CustomTick />}
          />
          <XAxis
            dataKey="total"
            type="number"
            width={1}
            height={1}
            padding={{ left: 180, right: 50 }}
            axisLine={false}
            tickLine={false}
          />
          <Bar type="number" dataKey="total" fill="#3F205A" barSize={20}>
            <LabelList
              id="owner-by-schooling-bar-label-list"
              dataKey="percentile"
              position="right"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const CustomLabel = ({ ...props }) => {
  const { x, y, width, height, value, offset } = props;
  const getXPos = (): number => {
    const windowWidth = window.screen.width;
    const xl = 1920;
    const l = 1536;
    const m = 1366;
    const p = 1200;
    const xp = 1024;
    if (windowWidth >= xl) return x + 720;
    if (windowWidth < xl && windowWidth >= l) return x + 575;
    if (windowWidth < l && windowWidth >= m) return x + 490;
    if (windowWidth < m && windowWidth >= p) return x + 410;
    if (windowWidth < p && windowWidth >= xp) return x + 323;
    return 0;
  };
  return (
    <text
      x={x}
      y={y}
      width={width}
      height={height}
      offset={offset}
      style={{ fontWeight: 700 }}
    >
      {value}
    </text>
  );
};

const CustomTick = ({ ...props }) => {
  const { x, y, width, height, payload } = props;
  const shouldBreak =
    payload.value === 'Sem instrução e fundamental incompleto';
  return shouldBreak ? (
    <g>
      <text
        x={x + 150}
        y={y - 10}
        width={width}
        height={height}
        textAnchor="end"
        style={{
          width: '150px',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-all',
          fontSize: '14',
        }}
      >
        Sem instrução e
      </text>
      <text
        x={x + 150}
        y={y + 20}
        width={width}
        height={height}
        textAnchor="end"
        style={{
          width: '150px',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-all',
          fontSize: '14',
        }}
      >
        fundamental incompleto
      </text>
    </g>
  ) : (
    <text
      x={x + 150}
      y={y}
      width={width}
      height={height}
      textAnchor="end"
      style={{
        width: '150px',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-all',
        fontSize: '14',
      }}
    >
      {payload.value}
    </text>
  );
};
